export default class TooltipBaseManager {
  constructor(editor) {
    this.editor = editor;

    this.tooltip = {};
    this.position = {};
    this.cachedLayer = null;
    this.resetTooltip();

    this.reverses = {
      right: "left",
      left: "right",
      top: "bottom",
      bottom: "top",
    };
  }

  resetTooltip() {
    this.tooltip = {
      visible: 0,
      type: "custom",
      style: {},
      classes: "",
      content: "",
    };
    this.cachedLayer = null;
    this.position = { x: null, y: null, transform: "" };
  }

  handleMouseMove(data) {
    if (!data.layer) {
      this.resetTooltip();
      return;
    }
    this.renderTooltip(data);
  }

  handleMouseDown() {
    this.resetTooltip();
    // handle mouse down functionality
  }
  handleMouseUp() {}

  handleZoom() {
    this.resetTooltip();
  }

  handleMove() {
    this.resetTooltip();
  }

  renderTooltip(data) {
    const { layer } = data;
    let tooltip = {};

    if (!this.isTooltipVisible(layer)) {
      this.resetTooltip();
      return;
    }

    if (this.shouldUpdateCachedLayer(layer)) {
      this.updateCachedLayer(layer);
    } else if (this.isNotCursorPosition(layer)) {
      return;
    }

    const hasPosition = this.calculatePosition(layer, data);
    if (hasPosition == false) {
      return;
    }

    tooltip.type = layer.tooltip.type;
    tooltip.content = layer.tooltip.content.html;
    tooltip.classes = this.getTooltipClasses(
      layer.tooltip.size,
      layer.tooltip.variant,
      layer.tooltip.position
    );
    tooltip.style = this.getTooltipStyle(
      layer.tooltip,
      this.position.x,
      this.position.y,
      this.position.transform
    );
    tooltip.visible = this.isTooltipVisible(layer);

    this.tooltip = tooltip;

    this.editor.functions.nextTick(() => {
      const { canvas } = this.editor.$refs;
      const outer = canvas.getBoundingClientRect();
      const tooltipElement = document.getElementById("tooltip");
      const tooltipBound = tooltipElement
        ? tooltipElement.getBoundingClientRect()
        : null;
      if (!tooltipBound) return;
      if (this.tooltipInBound(outer, tooltipBound)) {
        return;
      }

      this.updateTooltipPosition(layer, outer, tooltipBound);
    });
  }

  shouldUpdateCachedLayer(layer) {
    return (
      !this.cachedLayer ||
      this.cachedLayer.id !== layer.id ||
      this.cachedLayer.points !== layer.points ||
      this.cachedLayer.position !== layer.tooltip.position
    );
  }

  updateCachedLayer(layer) {
    this.cachedLayer = {
      id: layer.id,
      points: layer.points,
      position: layer.tooltip.position,
    };
  }

  isNotCursorPosition(layer) {
    return layer.tooltip.position != "cursor";
  }

  getRelationDelta() {
    // need to compensate difference between canvas and all document (for insertion)
    const canvas = this.editor.$refs.canvas;
    const canvasRect = canvas.getBoundingClientRect();
    const relationDeltaX = canvasRect.left - canvas.offsetLeft;
    const relationDeltaY = canvasRect.top - canvas.offsetTop;

    return { relationDeltaX, relationDeltaY };
  }
  calculatePosition(layer, data) {
    let { relationDeltaX, relationDeltaY } = this.getRelationDelta();
    if (layer.tooltip.position == "cursor") {
      this.position.x =
        data.event.clientX - relationDeltaX + layer.tooltip.margin.x * 1 + 10;
      this.position.y =
        data.event.clientY - relationDeltaY + layer.tooltip.margin.y * 1;
      this.position.transform = "";
      return true;
    } else {
      const preparedFixedPosition = this.prepareFixedPosition(
        layer,
        layer.tooltip.position
      );
      if (preparedFixedPosition == false) {
        return false;
      }

      preparedFixedPosition.x = preparedFixedPosition.x - relationDeltaX;
      preparedFixedPosition.y = preparedFixedPosition.y - relationDeltaY;
      this.position = preparedFixedPosition;
      return true;
    }
    return false;
  }

  tooltipInBound(outer, tooltipBound, threshold = 20) {
    if (
      tooltipBound.left < outer.left + threshold || // Left overflow
      tooltipBound.right > outer.right - threshold || // Right overflow
      tooltipBound.top < outer.top + threshold || // Top overflow
      tooltipBound.bottom > outer.bottom - threshold // Bottom overflow
    ) {
      return false;
    }
    return true;
  }

  updateTooltipPosition(layer, outer, tooltipBound, threshold = 20) {
    const { svgContainer } = this.editor.$refs;

    const imageRect = svgContainer.getBoundingClientRect();

    const { inside } = this.editor.functions;
    const { getZoom, getAspectRatio } = this.editor.getters;

    const bbox = layer.tool.getBBox(layer);
    const zoom = getZoom();
    const aspectRatio = getAspectRatio();

    let { left, top } = this.calculateReposition(
      layer,
      outer,
      tooltipBound,
      threshold
    );

    const editorCoords = {
      left: (left - imageRect.x) / zoom,
      top: (top - imageRect.y) / zoom,
    };
    let { transform } = this.position;

    if (
      !inside(
        [editorCoords.left, editorCoords.top],
        [
          [bbox.x, bbox.y],
          [bbox.x + bbox.width, bbox.y],
          [bbox.x + bbox.width, bbox.y + bbox.height],
          [bbox.x, bbox.y + bbox.height],
        ]
      )
    ) {
      const canvasCoords = {
        left: editorCoords.left * aspectRatio,
        top: editorCoords.top * aspectRatio,
      };

      const direction = this.getArrowDirection(
        bbox,
        canvasCoords.left,
        canvasCoords.top
      );
      const reversedDirection = this.reverses[direction];

      const preparedPosition = this.prepareFixedPosition(
        layer,
        reversedDirection
      );
      transform = preparedPosition.transform;

      const bound = this.calculateProposedBoundingRect(
        tooltipBound,
        preparedPosition
      );

      const canvasRect = this.editor.$refs.canvas.getBoundingClientRect();
      const canvasOffset = 10;
      left = bound.left;
      top = bound.top;

      if (reversedDirection === "left" || reversedDirection === "right") {
        if (top - bound.height / 2 < canvasRect.top + canvasOffset) {
          top = canvasRect.top + canvasOffset + bound.height / 2;
        }
      }

      if (reversedDirection === "left") {
        if (left > canvasRect.right) {
          left = canvasRect.right - canvasOffset;
        }
      }
      if (reversedDirection === "right") {
        if (left < canvasRect.left) {
          left = canvasRect.left + canvasOffset;
        }
      }

      if (reversedDirection === "top") {
        if (top > canvasRect.bottom) {
          top = canvasRect.bottom - canvasOffset;
        }
      }

      if (reversedDirection === "bottom") {
        if (top < canvasRect.top) {
          top = canvasRect.top + canvasOffset;
        }
      }

      if (reversedDirection === "left" || reversedDirection === "right") {
        if (
          top + bound.height / 2 >
          canvasRect.top + canvasRect.height - canvasOffset
        ) {
          top =
            canvasRect.top +
            canvasRect.height -
            canvasOffset -
            bound.height / 2;
        }
      }

      if (
        left + bound.width >
        canvasRect.left + canvasRect.width - canvasOffset
      ) {
        if (reversedDirection === "bottom" || reversedDirection === "top") {
          left =
            canvasRect.left + canvasRect.width - canvasOffset - bound.width / 2;
        }
      }

      if (left - bound.width / 2 < canvasRect.left + canvasOffset) {
        if (reversedDirection === "bottom" || reversedDirection === "top") {
          left = canvasRect.left + canvasOffset + bound.width / 2;
        }
      }

      this.tooltip.classes = this.getTooltipClasses(
        layer.tooltip.size,
        layer.tooltip.variant,
        reversedDirection,
        preparedPosition.transform
      );
    }
    let { relationDeltaX, relationDeltaY } = this.getRelationDelta();

    left -= relationDeltaX;
    top -= relationDeltaY;
    this.tooltip.style = this.getTooltipStyle(
      layer.tooltip,
      left,
      top,
      transform
    );
  }

  getArrowDirection(bbox, x, y) {
    const centerX = bbox.x + bbox.width / 2;
    const centerY = bbox.y + bbox.height / 2;
    return Math.abs(centerX - x) > Math.abs(centerY - y)
      ? centerX > x
        ? "right"
        : "left"
      : centerY > y
      ? "bottom"
      : "top";
  }

  calculateProposedBoundingRect(tooltipBound, preparedFixedPosition) {
    return {
      left: preparedFixedPosition.x,
      top: preparedFixedPosition.y,
      width: tooltipBound.width,
      height: tooltipBound.height,
      right: preparedFixedPosition.x + tooltipBound.width,
      bottom: preparedFixedPosition.y + tooltipBound.height,
    };
  }

  prepareFixedPosition(layer, position, threshold = 10) {
    const bbox = layer.tool.getBBox(layer);
    if (!bbox) return false;

    const offset = {
      x: layer.tooltip.margin.x ?? 0,
      y: layer.tooltip.margin.y ?? 0,
    };

    const svgContainer = this.editor.$refs.svgContainer.getBoundingClientRect();
    const zoom = this.editor.getters.getZoom();
    const aspectRatio = this.editor.getters.getAspectRatio();
    const baseX =
      svgContainer.left +
      ((bbox.x + bbox.width / 2 + offset.x) * zoom) / aspectRatio;
    const baseY =
      svgContainer.top +
      ((bbox.y + bbox.height / 2 + offset.y) * zoom) / aspectRatio;

    const positions = {
      bottom: {
        x: baseX,
        y: baseY + ((bbox.height / 2) * zoom) / aspectRatio + threshold,
        transform: "translate(-50%, 0)",
      },
      top: {
        x: baseX,
        y: baseY - ((bbox.height / 2) * zoom) / aspectRatio - threshold,
        transform: "translate(-50%, -100%)",
      },
      left: {
        x: baseX - ((bbox.width / 2) * zoom) / aspectRatio - threshold,
        y: baseY,
        transform: "translate(-100%, -50%)",
      },
      right: {
        x: baseX + ((bbox.width / 2) * zoom) / aspectRatio + threshold,
        y: baseY,
        transform: "translate(0, -50%)",
      },
    };

    const selectedPosition = positions[position] || positions.top;

    return {
      x: selectedPosition.x,
      y: selectedPosition.y,
      transform: selectedPosition.transform,
    };
  }

  calculateReposition(layer, outer, tooltipBound, threshold = 20) {
    let { left, top } = tooltipBound;
    const position = layer.tooltip.position;

    if (tooltipBound.left < outer.left + threshold)
      left = outer.left + threshold;
    if (tooltipBound.right > outer.right - threshold)
      left = outer.right - tooltipBound.width - threshold;
    if (tooltipBound.top < outer.top + threshold) top = outer.top + threshold;
    if (tooltipBound.bottom > outer.bottom - threshold)
      top = outer.bottom - tooltipBound.height - threshold;

    switch (position) {
      case "top":
        top += tooltipBound.height; // Смещаем тултип выше элемента
        left += tooltipBound.width / 2; // Центрируем по горизонтали
        break;
      case "bottom":
        // top -= 0; // Смещаем тултип ниже элемента
        left += tooltipBound.width / 2; // Центрируем по горизонтали
        break;
      case "left":
        left += tooltipBound.width; // Смещаем тултип влево от элемента
        top += tooltipBound.height / 2; // Центрируем по вертикали
        break;
      case "right":
        // left += tooltipBound.width + threshold; // Смещаем тултип вправо от элемента
        top += tooltipBound.height / 2; // Центрируем по вертикали
        break;
    }
    let { relationDeltaX, relationDeltaY } = this.getRelationDelta();
    left = left;
    top = top;
    return { left, top };
  }

  getTooltipClasses(size, variant, position, transform) {
    let classes = "";
    classes += `interactive-tooltip-${size} `;
    classes += `interactive-tooltip-variant-${variant} `;
    if (position) {
      switch (position) {
        case "top":
          classes += `top`;
          break;
        case "bottom":
          classes += `bottom`;
          break;
        case "left":
          classes += `left`;
          break;
        case "right":
          classes += `right`;
          break;
        default:
        // if (transform == "") {
        //   classes += `cursor`;
        // } else {
        //   classes += `top`;
        // }
      }
    }
    return classes;
  }

  isTooltipVisible(layer) {
    if (layer.visible === 0) return false;
    if (!layer.tooltip) return false;
    if (layer.tooltip.type == "custom" && layer.tooltip.content.html == null)
      return false;

    return layer.tooltip.visible & this.editor.getters.getBreakpoint();
  }

  getTooltipStyle(tooltipOptions, positionX, positionY, transform) {
    return {
      padding: `${tooltipOptions.padding.y}px ${tooltipOptions.padding.x}px`,
      left: `${positionX}px`,
      top: `${positionY}px`,
      transform: transform,
      display: "block",
    };
  }
}
